import React, { memo, useEffect, useState } from "react";
import {
  Box,
  Button,
  Header,
  Icon,
  Link,
  SpaceBetween,
  Spinner,
  Tabs,
} from "@cloudscape-design/components";
import {
  IToolbarButtonActiveContentType,
  IToolbarButtonProps,
  IToolbarButtonType,
  ToolbarActiveStateContentProps,
  ToolbarContainerItemContent,
  useToolbarCollapseStore,
  useToolbarMetricsStore,
} from "@amzn/aws-euc-ui";
import {
  FEEDBACK_EMAIL_ADDRESS,
  ToolbarItemId,
  ToolbarProfileTabId,
} from "../../../../constants/Toolbar";
import { AppStreamSDK } from "../../../../utils/AppStreamSDK";
import { Trans, useTranslation } from "react-i18next";
import { SessionPerformanceTabContent } from "./SessionPerformanceTabContent";
import { ValueWithLabel } from "../../ValueWithLabel";
import { HelpTabContent } from "./HelpTabContent";
import { useToolbarProfileTabIdStore } from "../../../../hooks/useToolbarProfileTabIdStore";
import { CopyButton } from "../../../general/CopyButton";
import { useSessionDataStore } from "../../../../hooks/useSessionDataStore";
import log, { TOOLBAR_METRIC_NAME } from "../../../../logging";
import { useEmitSessionMetrics } from "../../../../hooks/useEmitSessionMetrics";

/**
 * Profile - About WorkSpaces Web tab content
 */
const AboutTabContent = (): JSX.Element => {
  return (
    <ValueWithLabel label={"WorkSpaces Web"} valueTestId={"about-wsw"}>
      <Trans i18nKey={"toolbar.item.user.content.tab.about.content"} />
    </ValueWithLabel>
  );
};

const EmailCopyButton = (props: { children?: React.ReactNode }) => {
  const { t } = useTranslation();

  return (
    <CopyButton
      copyText={FEEDBACK_EMAIL_ADDRESS}
      successPopoverText={t(
        "toolbar.item.user.content.tab.feedback.email.popover.success"
      )}
      errorPopoverText={t(
        "toolbar.item.user.content.tab.feedback.email.popover.failed"
      )}
      copyIconAriaLabel={t(
        "toolbar.item.user.content.tab.feedback.email.copy.button.ariaLabel"
      )}
    >
      <Link href={`mailto:${FEEDBACK_EMAIL_ADDRESS}`}>{props.children}</Link>
    </CopyButton>
  );
};

/**
 * Profile - Feedback tab content
 */
const FeedbackTabContent = (): JSX.Element => {
  const { t } = useTranslation();

  const sessionId = useSessionDataStore((store) => store.sessionID);

  return (
    <div data-testid={"feedback-tab"}>
      <SpaceBetween size="l">
        <Trans
          i18nKey={"toolbar.item.user.content.tab.feedback.description"}
          values={{
            emailAddress: FEEDBACK_EMAIL_ADDRESS,
          }}
        >
          {[<EmailCopyButton></EmailCopyButton>]}
        </Trans>
        <Box>
          <Trans
            i18nKey={
              "toolbar.item.user.content.tab.feedback.sessionId.description"
            }
          ></Trans>
        </Box>
        <ValueWithLabel
          label={t("toolbar.item.user.content.tab.feedback.sessionId.label")}
        >
          {sessionId == null ? (
            <Spinner data-testid={"loading-session-data-sessionId"} />
          ) : (
            <>
              <CopyButton
                copyText={sessionId}
                successPopoverText={
                  <Trans
                    i18nKey={
                      "toolbar.item.user.content.tab.feedback.sessionId.popover.success"
                    }
                  />
                }
                errorPopoverText={
                  <Trans
                    i18nKey={
                      "toolbar.item.user.content.tab.feedback.sessionId.popover.failed"
                    }
                  />
                }
                copyIconAriaLabel={t(
                  "toolbar.item.user.content.tab.feedback.sessionId.copy.button.ariaLabel"
                )}
              />
              <>{sessionId}</>
            </>
          )}
        </ValueWithLabel>
      </SpaceBetween>
    </div>
  );
};

export const useProfileToolbarItem = (
  appStreamSDK: AppStreamSDK
): IToolbarButtonProps => {
  const { t } = useTranslation();

  const activeTabId = useToolbarProfileTabIdStore((store) => store.activeId);
  const setActiveTabId = useToolbarProfileTabIdStore(
    (store) => store.setActiveId
  );

  const { setCollapsed } = useToolbarCollapseStore();
  const [isSessionEnded, setSessionEnded] = useState(false);
  useEmitSessionMetrics(isSessionEnded);

  return {
    type: IToolbarButtonType.STATEFUL,
    activeStateContentType: IToolbarButtonActiveContentType.CONTAINER,
    id: ToolbarItemId.PROFILE,
    label: t("toolbar.item.user.label"),
    ariaLabel: t("toolbar.item.user.ariaLabel"),
    icon: <Icon data-testid="profile-icon" name="user-profile" />,
    onClick: () => {
      setCollapsed(false);
    },
    ActiveStateContent: memo((props: ToolbarActiveStateContentProps) => (
      <ToolbarContainerItemContent
        close={props.close}
        closeButtonAriaLabel={t("toolbar.item.user.content.close.ariaLabel")}
        closeButtonTitle={t("toolbar.item.content.close.title")}
      >
        <SpaceBetween direction={"horizontal"} size={"m"}>
          <Header>
            <Trans i18nKey={"toolbar.item.user.label"} />
          </Header>
          <Button
            variant="primary"
            onClick={() => {
              setSessionEnded(true);
              appStreamSDK.endSession();
            }}
          >
            <Trans i18nKey={"toolbar.item.user.content.signOut.button.label"} />
          </Button>
        </SpaceBetween>
        <Tabs
          onChange={({ detail }) => {
            setActiveTabId(detail.activeTabId as ToolbarProfileTabId);
          }}
          activeTabId={activeTabId}
          tabs={[
            {
              label: t(
                "toolbar.item.user.content.tab.sessionPerformance.label"
              ),
              id: ToolbarProfileTabId.SESSION_PERFORMANCE,
              content: <SessionPerformanceTabContent />,
            },
            {
              label: t("toolbar.item.user.content.tab.feedback.label"),
              id: ToolbarProfileTabId.FEEDBACK,
              content: <FeedbackTabContent />,
            },
            {
              label: t("toolbar.item.user.content.tab.help.label"),
              id: ToolbarProfileTabId.HELP,
              content: <HelpTabContent />,
            },
            {
              label: t("toolbar.item.user.content.tab.about.label"),
              id: ToolbarProfileTabId.ABOUT,
              content: <AboutTabContent />,
            },
          ]}
        />
      </ToolbarContainerItemContent>
    )),
  };
};
